import { promiseWrapper } from 'utils/promise'
import { CalculatorDirection } from 'models/calculator'

declare let USER_BASE_URL_IK: string

export interface CurrencyBankRate {
  buy: number
  sell: number
  spread: string
}

export type CurrencyBankRateMap = Record<string, CurrencyBankRate>

export type BankRateMap = Record<string, CurrencyBankRateMap>

export interface BanksRatesRest {
  valid_from: string
}

export type BanksRates = BanksRatesRest & BankRateMap

const bankIdtoSupportedBankId = {
  bzwbk: 'wbk',
  citibank: 'citi',
  mbank: 'grupabre',
  millennium: 'millenium',
  pkobp: 'pko',
}

const bankRateHelper = {
  createBankRateMap: (bankIdArray: string[], banks: BankRateMap): BankRateMap =>
    bankIdArray.reduce(
      (previousVal, currentVal) => ({
        ...previousVal,
        ...{
          [currentVal]: banks[currentVal],
        },
      }),
      {},
    ),
  sortBanksBySellRate: (banks: BankRateMap, currency: string): BankRateMap => {
    const sortedKeys = Object.keys(banks).sort((a, b) => (banks[a][currency].sell < banks[b][currency].sell ? 1 : -1))
    return bankRateHelper.createBankRateMap(sortedKeys, banks)
  },
  filterBanksByCurrency: (banks: BankRateMap, currency: string): BankRateMap => {
    const banksForCurrency = Object.keys(banks).filter((bankId) => banks[bankId][currency])
    return bankRateHelper.createBankRateMap(banksForCurrency, banks)
  },
  filterBanksByRate: (banks: BankRateMap, direction: CalculatorDirection, rate: number, currency: string) => {
    const rateDirection = direction === 'BUY' ? 'sell' : 'buy'
    const banksByRate = Object.keys(banks).filter((bankId) => {
      if (!banks[bankId][currency] || !banks[bankId][currency][rateDirection]) {
        return true
      }
      return rateDirection === 'buy'
        ? banks[bankId][currency][rateDirection] <= rate
        : banks[bankId][currency][rateDirection] >= rate
    })
    return bankRateHelper.createBankRateMap(banksByRate, banks)
  },
  filterForbiddenBanks: (banks: BankRateMap): BankRateMap => {
    const filteredBanksKeys = Object.keys(banks).filter((bankId) => !bankRateHelper.isForbiddenBank(bankId))
    return bankRateHelper.createBankRateMap(filteredBanksKeys, banks)
  },
  isForbiddenBank: (bankId: string): boolean => ['eurobank', 'polbank', 'raiffeisen', 'bgz'].includes(bankId),
  mapIdToSupportedBankId: (bankId: string): string | undefined => bankIdtoSupportedBankId[bankId],
  mapSupportedBankIdToBankRate: (supportedBankId: string): string | undefined => {
    const index = Object.values(bankIdtoSupportedBankId).indexOf(supportedBankId)
    return Object.keys(bankIdtoSupportedBankId)[index]
  },
  fetchBankRates: (): Promise<BanksRates> =>
    promiseWrapper<BanksRates, null>(`${USER_BASE_URL_IK}/api/public/bankRates`, 'GET').then((data) => {
      const { valid_from, ...banks } = data
      return {
        valid_from,
        ...bankRateHelper.filterForbiddenBanks(banks),
      } as BanksRates
    }),
}

export default bankRateHelper
